/**
 * @module pages/AdminPage
 * @description Content admin: story cards and thank-you cards (full CRUD,
 * images, content hold), the pledge ledger with CSV export and offline
 * totals, and device settings (edition, default print size).
 *
 * // TEMPORARY: no authentication in the demo build.
 * What caused this: auth depends on where AFJ hosts it (WordPress login,
 * or Cloudflare Access in front of a Pages/Worker deploy), which is decided
 * after the demo. Why chosen: showing the workflow now matters more than
 * gating a browser-local demo. Side-effects: anyone with the preview URL can
 * open #/admin, but edits only affect their own browser.
 */
import { useMemo, useState, type ReactNode } from "react";
import { StoryPostcard } from "../components/StoryPostcard";
import { StoryCardBack } from "../components/CardBacks";
import { ThankYouBack, ThankYouFront } from "../components/ThankYouPostcard";
import {
    cardRepository,
    pledgeRepository,
    settingsRepository,
    thankYouRepository,
    useCards,
    useOfflineCount,
    useOfflineTotal,
    usePledges,
    useSettings,
    useThankYouCards,
} from "../data";
import type { CollectionRepository } from "../data/repositories";
import { CAMPAIGN, CAUSE_LABELS, PAYMENT_INTENT_LABELS, formatUsd } from "../config/campaign";
import { DEFAULT_EDITION_ID, EDITIONS } from "../config/editions";
import { PRINT_SIZES, type PrintSizeId } from "../config/print";
import { computeMatchProgress } from "../domain/match";
import { editionSource, useEdition } from "../app/edition";
import { FixedLandscape } from "../app/cardOrientation";
import { hrefFor } from "../app/router";
import { PHOTO_ASPECT, PORTRAIT_ASPECT, PRINT_MIN_WIDTH_PX, prepareCardImage } from "../lib/imageUpload";
import { assetUrl } from "../lib/assetUrl";
import { toCsv } from "../lib/csv";
import { downloadBlob } from "../lib/outputEngine";
import type { CardImage, EditionId, OrderedRecord, StoryCard, ThankYouCard } from "../domain/types";

type Tab = "cards" | "thank-you" | "pledges" | "settings";

const TABS: { id: Tab; label: string }[] = [
    { id: "cards", label: "Story cards" },
    { id: "thank-you", label: "Thank-you cards" },
    { id: "pledges", label: "Pledges" },
    { id: "settings", label: "Settings" },
];

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** A blank story card for "Add card"; order goes to the end of the deck. */
const newStoryCard = (order: number): StoryCard => ({
    id: crypto.randomUUID(),
    slug: `new-story-${order}`,
    order,
    title: "New Story",
    statValue: "0",
    statLabel: "PEOPLE REACHED",
    body: "Describe the impact in one or two sentences.",
    image: { src: "cards/mission-of-sight.jpg", alt: "New story — illustrative image", isIllustrative: true },
    published: false,
    contentHold: false,
});

/** A blank thank-you card, pre-filled with the shared front wording from the first seeded card. */
const newThankYouCard = (order: number, template: ThankYouCard | undefined): ThankYouCard => ({
    id: crypto.randomUUID(),
    slug: `thank-you-${order}`,
    order,
    published: false,
    contentHold: true,
    name: "New thank-you card",
    place: "School, Parish",
    image: template?.image ?? { src: "thank-you/browns-town-front.jpg", alt: "", isIllustrative: false },
    headline: template?.headline ?? "You sent your love.",
    subhead: template?.subhead ?? "Jamaica sends it back.",
    body: template?.body ?? "",
    turnOverLine: template?.turnOverLine ?? "Turn over. Someone here wanted to say thank you.",
    backKicker: "A thank-you from …",
    note: { text: "Dear Friends in America,", scanSrc: null, caption: "Name, School, Parish" },
    message: template?.message ?? "",
    closingLine: template?.closingLine ?? "Thank you for being part of the story.",
    personalNote: "",
    recipient: "",
});

export function AdminPage() {
    const [tab, setTab] = useState<Tab>("cards");
    const edition = useEdition();
    return (
        <section className="admin">
            <header className="admin-head">
                <div>
                    <p className="pc-kicker">Admin · {edition.label} edition</p>
                    <h1 className="section-title">{CAMPAIGN.campaignName} — content & pledges</h1>
                </div>
                <div className="admin-head-actions">
                    <a className="btn btn--primary" href={hrefFor({ name: "print" })}>Print studio</a>
                    <div className="admin-tabs" role="tablist">
                        {TABS.map((t) => (
                            <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</button>
                        ))}
                    </div>
                </div>
            </header>
            {tab === "cards" && <StoryCardsAdmin />}
            {tab === "thank-you" && <ThankYouAdmin />}
            {tab === "pledges" && <PledgesAdmin />}
            {tab === "settings" && <SettingsAdmin />}
        </section>
    );
}

/* ------------------------------------------------------------------ */
/* Shared collection editor (story cards and thank-you cards)          */
/* ------------------------------------------------------------------ */

interface CollectionAdminProps<T extends OrderedRecord> {
    records: T[];
    repository: CollectionRepository<T>;
    create: (order: number) => T;
    resetLabel: string;
    listTitle: (r: T) => string;
    listMeta: (r: T) => string;
    thumb: (r: T) => string;
    editor: (record: T, save: (r: T) => void, remove: () => void) => ReactNode;
    preview: (record: T) => ReactNode;
}

/** List + editor + live preview, generic over any ordered collection (DRY). */
function CollectionAdmin<T extends OrderedRecord>(props: CollectionAdminProps<T>) {
    const { records, repository } = props;
    const [selectedId, setSelectedId] = useState<string | null>(records[0]?.id ?? null);
    const selected = records.find((r) => r.id === selectedId) ?? null;
    const [error, setError] = useState<string | null>(null);

    /** Every edit saves immediately; the preview and public pages update live. */
    const save = (record: T) => {
        try {
            repository.save(record);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    const add = () => {
        const record = props.create(records.length + 1);
        save(record);
        setSelectedId(record.id);
    };

    const remove = (record: T) => {
        if (!window.confirm(`Delete "${props.listTitle(record)}"? This cannot be undone.`)) return;
        repository.remove(record.id);
        setSelectedId(records.find((r) => r.id !== record.id)?.id ?? null);
    };

    return (
        <div className="admin-cards">
            <aside className="admin-list">
                <div className="admin-list-actions">
                    <button className="btn btn--primary" onClick={add}>+ Add</button>
                    <button className="btn" onClick={() => window.confirm(`${props.resetLabel}? Your edits will be replaced.`) && repository.resetToSeed()}>
                        Reset
                    </button>
                </div>
                <ol>
                    {records.map((r) => (
                        <li key={r.id}>
                            <button className="admin-list-item" aria-current={r.id === selectedId} onClick={() => setSelectedId(r.id)}>
                                <img src={assetUrl(props.thumb(r))} alt="" />
                                <span>
                                    <strong>{props.listTitle(r)}</strong>
                                    <small>
                                        {props.listMeta(r)} · {r.contentHold ? "Content hold" : r.published ? "Published" : "Draft"}
                                    </small>
                                </span>
                            </button>
                        </li>
                    ))}
                </ol>
            </aside>

            {selected ? (
                <div className="admin-editor">
                    <div key={selected.id}>{props.editor(selected, save, () => remove(selected))}</div>
                    {error && <p className="pf-error" role="alert">{error}</p>}
                    <div className="admin-preview">
                        {/* Admin previews show the printed card. */}
                        <FixedLandscape>{props.preview(selected)}</FixedLandscape>
                    </div>
                </div>
            ) : (
                <p>Nothing here yet. Add one to begin.</p>
            )}
        </div>
    );
}

/** Image field: uploads, crops to the frame, and warns when a photo is too small for print. */
function ImageField({ label, image, aspect, onChange }: { label: string; image: CardImage; aspect: number | null; onChange: (img: CardImage) => void }) {
    const [note, setNote] = useState("");
    const upload = async (file: File | undefined) => {
        if (!file) return;
        try {
            setNote("Processing image…");
            const { dataUrl, sourceWidth } = await prepareCardImage(file, aspect);
            onChange({ ...image, src: dataUrl });
            setNote(
                sourceWidth < PRINT_MIN_WIDTH_PX
                    ? `Uploaded. Heads-up: ${sourceWidth}px wide is fine on screen but soft in print (aim for ${PRINT_MIN_WIDTH_PX}px+).`
                    : "Uploaded — print-ready resolution."
            );
        } catch (e) {
            setNote(e instanceof Error ? e.message : String(e));
        }
    };
    return (
        <>
            <div className="pf-grid2">
                <label className="pf-field">
                    <span>{label}</span>
                    <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
                </label>
                <label className="pf-field">
                    <span>Image description (alt text)</span>
                    <input value={image.alt} onChange={(e) => onChange({ ...image, alt: e.target.value })} />
                </label>
            </div>
            <label className="pf-check">
                <input type="checkbox" checked={image.isIllustrative} onChange={(e) => onChange({ ...image, isIllustrative: e.target.checked })} />
                <span>Show "Illustrative image" badge</span>
            </label>
            {note && <p className="pf-fine" aria-live="polite">{note}</p>}
        </>
    );
}

/** Published / content hold / order / delete — the same for every record type. */
function RecordToggles<T extends OrderedRecord>({ record, onChange, onDelete }: { record: T; onChange: (r: T) => void; onDelete: () => void }) {
    return (
        <div className="admin-toggles">
            <label className="pf-check">
                <input type="checkbox" checked={record.published} onChange={(e) => onChange({ ...record, published: e.target.checked })} />
                <span>Published</span>
            </label>
            <label className="pf-check">
                <input type="checkbox" checked={!!record.contentHold} onChange={(e) => onChange({ ...record, contentHold: e.target.checked })} />
                <span>Content hold (not public, prints only as PROOF)</span>
            </label>
            <label className="pf-field pf-field--inline">
                <span>Order</span>
                <input type="number" min={1} value={record.order} onChange={(e) => onChange({ ...record, order: Number(e.target.value) })} />
            </label>
            <button type="button" className="btn btn--danger" onClick={onDelete}>Delete</button>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Story cards                                                          */
/* ------------------------------------------------------------------ */

function StoryCardsAdmin() {
    return (
        <CollectionAdmin<StoryCard>
            records={useCards()}
            repository={cardRepository}
            create={newStoryCard}
            resetLabel="Restore the approved story cards"
            listTitle={(c) => c.title}
            listMeta={(c) => c.statValue.replace(/\n/g, " ")}
            thumb={(c) => c.image.src}
            editor={(card, save, remove) => <StoryCardEditor card={card} onChange={save} onDelete={remove} />}
            preview={(card) => (
                <>
                    <p className="pc-kicker">Live preview · front</p>
                    <StoryPostcard card={card} />
                    <p className="pc-kicker">Back</p>
                    <StoryCardBack card={card} />
                </>
            )}
        />
    );
}

function StoryCardEditor({ card, onChange, onDelete }: { card: StoryCard; onChange: (c: StoryCard) => void; onDelete: () => void }) {
    const set = <K extends keyof StoryCard>(key: K, value: StoryCard[K]) => onChange({ ...card, [key]: value });
    return (
        <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
            <label className="pf-field">
                <span>Title</span>
                <input value={card.title} onChange={(e) => onChange({ ...card, title: e.target.value, slug: slugify(e.target.value) || card.slug })} />
            </label>
            <div className="pf-grid2">
                <label className="pf-field">
                    <span>Headline figure (Enter for a second line)</span>
                    <textarea rows={2} value={card.statValue} onChange={(e) => set("statValue", e.target.value)} />
                </label>
                <label className="pf-field">
                    <span>Figure caption</span>
                    <input value={card.statLabel} onChange={(e) => set("statLabel", e.target.value.toUpperCase())} />
                </label>
            </div>
            <label className="pf-field">
                <span>Story ({card.body.length} characters · aim for ≤ 230)</span>
                <textarea rows={4} value={card.body} onChange={(e) => set("body", e.target.value)} />
            </label>
            <ImageField label="Featured image" image={card.image} aspect={PHOTO_ASPECT} onChange={(img) => set("image", img)} />
            <RecordToggles record={card} onChange={onChange} onDelete={onDelete} />
        </form>
    );
}

/* ------------------------------------------------------------------ */
/* Thank-you cards                                                      */
/* ------------------------------------------------------------------ */

function ThankYouAdmin() {
    const records = useThankYouCards();
    return (
        <>
            <p className="admin-note">
                Thank-you postcards are mailed after the Gala, not shown on the public site.{" "}
                <a href={hrefFor({ name: "thank-you", slug: null })}>Review them full size →</a>
            </p>
            <CollectionAdmin<ThankYouCard>
                records={records}
                repository={thankYouRepository}
                create={(order) => newThankYouCard(order, records[0])}
                resetLabel="Restore the seeded thank-you cards"
                listTitle={(c) => c.name}
                listMeta={(c) => c.place}
                thumb={(c) => c.image.src}
                editor={(card, save, remove) => <ThankYouEditor card={card} onChange={save} onDelete={remove} />}
                preview={(card) => (
                    <>
                        <p className="pc-kicker">Live preview · front</p>
                        <ThankYouFront card={card} />
                        <p className="pc-kicker">Back</p>
                        <ThankYouBack card={card} />
                    </>
                )}
            />
        </>
    );
}

function ThankYouEditor({ card, onChange, onDelete }: { card: ThankYouCard; onChange: (c: ThankYouCard) => void; onDelete: () => void }) {
    const set = <K extends keyof ThankYouCard>(key: K, value: ThankYouCard[K]) => onChange({ ...card, [key]: value });
    const field = (key: "name" | "place" | "headline" | "subhead" | "turnOverLine" | "backKicker" | "closingLine", label: string) => (
        <label className="pf-field">
            <span>{label}</span>
            <input value={card[key]} onChange={(e) => set(key, e.target.value)} />
        </label>
    );
    const [scanNote, setScanNote] = useState("");
    const uploadScan = async (file: File | undefined) => {
        if (!file) return;
        try {
            const { dataUrl } = await prepareCardImage(file, null);
            set("note", { ...card.note, scanSrc: dataUrl });
            setScanNote("Scan uploaded.");
        } catch (e) {
            setScanNote(e instanceof Error ? e.message : String(e));
        }
    };
    return (
        <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
            <div className="pf-grid2">
                {field("name", "Admin name")}
                {field("place", "Front title (place)")}
            </div>
            <ImageField label="Front photo (portrait)" image={card.image} aspect={PORTRAIT_ASPECT} onChange={(img) => set("image", img)} />
            <div className="pf-grid2">
                {field("headline", "Headline")}
                {field("subhead", "Subhead")}
            </div>
            <label className="pf-field">
                <span>Front paragraph</span>
                <textarea rows={3} value={card.body} onChange={(e) => set("body", e.target.value)} />
            </label>
            {field("turnOverLine", "Italic line under the rule")}
            <h3 className="admin-sub">Back</h3>
            {field("backKicker", "Back kicker")}
            <label className="pf-field">
                <span>Child's note (typed; used when there is no scan)</span>
                <textarea rows={4} value={card.note.text} onChange={(e) => set("note", { ...card.note, text: e.target.value })} />
            </label>
            <div className="pf-grid2">
                <label className="pf-field">
                    <span>Scan of the real note (replaces typed note)</span>
                    <input type="file" accept="image/*" onChange={(e) => uploadScan(e.target.files?.[0])} />
                </label>
                <label className="pf-field">
                    <span>Note caption</span>
                    <input value={card.note.caption} onChange={(e) => set("note", { ...card.note, caption: e.target.value })} />
                </label>
            </div>
            {card.note.scanSrc && (
                <button type="button" className="btn btn--sm" onClick={() => set("note", { ...card.note, scanSrc: null })}>Use typed note instead of scan</button>
            )}
            {scanNote && <p className="pf-fine">{scanNote}</p>}
            <label className="pf-field">
                <span>Back message</span>
                <textarea rows={3} value={card.message} onChange={(e) => set("message", e.target.value)} />
            </label>
            {field("closingLine", "Closing line (italic)")}
            <div className="pf-grid2">
                <label className="pf-field">
                    <span>Host's handwritten line (leave empty to write by hand)</span>
                    <textarea rows={2} value={card.personalNote} onChange={(e) => set("personalNote", e.target.value)} />
                </label>
                <label className="pf-field">
                    <span>Recipient (leave empty to address by hand)</span>
                    <textarea rows={3} value={card.recipient} onChange={(e) => set("recipient", e.target.value)} />
                </label>
            </div>
            <RecordToggles record={card} onChange={onChange} onDelete={onDelete} />
        </form>
    );
}

/* ------------------------------------------------------------------ */
/* Pledges                                                              */
/* ------------------------------------------------------------------ */

function PledgesAdmin() {
    const pledges = usePledges();
    const offline = useOfflineTotal();
    const offlineCount = useOfflineCount();
    const edition = useEdition();
    const progress = useMemo(() => computeMatchProgress(pledges, CAMPAIGN.matchCap, offline, offlineCount), [pledges, offline, offlineCount]);

    const exportCsv = () => {
        const rows = pledges.map((p) => ({
            created: p.createdAt,
            name: p.name,
            email: p.email,
            phone: p.phone,
            amount: p.amount,
            frequency: p.frequency,
            howGiving: PAYMENT_INTENT_LABELS[p.paymentIntent],
            anonymous: p.anonymous ? "yes" : "no",
            table: p.tableNumber,
            causes: p.causes.map((c) => CAUSE_LABELS[c]).join("; "),
            majorGiftConversation: p.wantsMajorGiftConversation ? "yes" : "no",
            sourceCard: p.sourceCardSlug ?? "",
            agreedToTerms: p.agreedToTerms ? "yes" : "no",
            edition: p.edition ?? "",
        }));
        downloadBlob(new Blob([toCsv(rows)], { type: "text/csv" }), `afj-pledges-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    return (
        <div className="admin-pledges">
            <div className="admin-stats">
                <div><span>Given</span><strong>{formatUsd(progress.raised)}</strong></div>
                <div><span>Friends who have given</span><strong>{progress.donorCount}</strong></div>
                {edition.showMatch && (
                    <>
                        <div><span>Match unlocked</span><strong>{formatUsd(progress.unlocked)}</strong></div>
                        <div><span>Total with match</span><strong>{formatUsd(progress.totalImpact)}</strong></div>
                    </>
                )}
            </div>
            <div className="admin-list-actions">
                <label className="pf-field pf-field--inline">
                    <span>Offline / paper gifts ($)</span>
                    <input type="number" min={0} value={offline} onChange={(e) => pledgeRepository.setOfflineTotal(Number(e.target.value))} />
                </label>
                <label className="pf-field pf-field--inline">
                    <span>Number of offline gifts</span>
                    <input type="number" min={0} value={offlineCount} onChange={(e) => pledgeRepository.setOfflineCount(Number(e.target.value))} />
                </label>
                <button className="btn btn--primary" onClick={exportCsv} disabled={!pledges.length}>Export CSV (Neon import)</button>
                <button className="btn btn--danger" onClick={() => window.confirm("Clear all demo pledges?") && pledgeRepository.clear()} disabled={!pledges.length}>
                    Clear demo pledges
                </button>
            </div>
            <div className="table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr><th>When</th><th>Name</th><th>Amount</th><th>How</th><th>Table</th><th>Causes</th><th>Source</th><th /></tr>
                    </thead>
                    <tbody>
                        {pledges.map((p) => (
                            <tr key={p.id}>
                                <td>{new Date(p.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                                <td>{p.name}{p.anonymous && " (anon.)"}<br /><small>{p.email}</small></td>
                                <td className="num">{formatUsd(p.amount)}{p.frequency === "monthly" && "/mo"}</td>
                                <td>{PAYMENT_INTENT_LABELS[p.paymentIntent]}</td>
                                <td>{p.tableNumber}</td>
                                <td>{p.causes.map((c) => CAUSE_LABELS[c]).join(", ")}{p.wantsMajorGiftConversation && " ★"}</td>
                                <td>{p.sourceCardSlug ?? "—"}</td>
                                <td><button className="btn btn--danger btn--sm" onClick={() => pledgeRepository.remove(p.id)}>Remove</button></td>
                            </tr>
                        ))}
                        {!pledges.length && <tr><td colSpan={8}>No pledges yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Settings                                                             */
/* ------------------------------------------------------------------ */

const SOURCE_LABELS = {
    url: "the ?edition= link you opened",
    device: "the choice saved on this device",
    build: "this deploy's build setting",
    default: "the built-in default",
} as const;

function SettingsAdmin() {
    const settings = useSettings();
    const active = useEdition();
    const source = editionSource(settings.editionOverride);
    const choose = (value: EditionId | null) => settingsRepository.update({ editionOverride: value });
    const base = `${window.location.origin}${window.location.pathname}`;

    return (
        <div className="admin-settings">
            <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
                <fieldset className="pf-section">
                    <legend className="pf-legend">Edition</legend>
                    <p>
                        Active: <strong>{active.label}</strong>, set by {SOURCE_LABELS[source]}.
                    </p>
                    <label className="pf-check">
                        <input type="radio" name="edition" checked={settings.editionOverride === null} onChange={() => choose(null)} />
                        <span>Use this deploy's default ({EDITIONS[DEFAULT_EDITION_ID].label} unless the build says otherwise)</span>
                    </label>
                    {Object.values(EDITIONS).map((ed) => (
                        <label key={ed.id} className="pf-check">
                            <input type="radio" name="edition" checked={settings.editionOverride === ed.id} onChange={() => choose(ed.id)} />
                            <span><strong>{ed.label}</strong> — {ed.summary}</span>
                        </label>
                    ))}
                    <p className="pf-fine">
                        This choice applies to this browser only. To switch everyone, redeploy with VITE_AFJ_EDITION set, or share a
                        link: <a href={`${base}?edition=gala`}>Gala</a> · <a href={`${base}?edition=campaign`}>Campaign</a>.
                    </p>
                </fieldset>
                <fieldset className="pf-section">
                    <legend className="pf-legend">Default print size</legend>
                    {Object.values(PRINT_SIZES).map((s) => (
                        <label key={s.id} className="pf-check">
                            <input type="radio" name="print-size" checked={settings.printSize === s.id} onChange={() => settingsRepository.update({ printSize: s.id as PrintSizeId })} />
                            <span><strong>{s.label}</strong> — {s.mailing}</span>
                        </label>
                    ))}
                </fieldset>
            </form>
        </div>
    );
}
